import intl from 'react-intl-universal'
import _ from 'lodash'
import { NEW_EDITOR_EXP } from '~/util/constants/keys'
import {
  NEW_STORY_EDITOR_UI_ROUTE,
  NEW_STORY_WITH_TEMPLATE_EDITOR_UI_ROUTE,
  EDIT_STORY_EDITOR_UI_ROUTE,
} from '~/util/constants/uiRoutes'
import {
  lockDetailsAction,
  storyLockedAccessor,
  getLockDetails,
} from '~/redux/appReducer'
import { getDistributors } from '../services/distributor'
import {
  getDocumentLockDetails,
  acquireDocumentLock,
  setIsValidTab,
} from '~/reactapp/App/LockManager/lock-utils'
import { setLastActiveTime } from '~/reactapp/App/LockManager/activity-utils'

export function handlePicklistsError(error, toastr) {
  console.log('Picklist error', error)
  if (error.status === 404) {
    console.log('No picklists appear to be set up for this organization.')
  } else {
    toastr.error(intl.get('initialLoads.picklist.error'))
  }
  return {
    picklists: {},
  }
}

export const websites = [
  'toastr',
  'siteService',
  (toastr, { getWebsites }) => {
    return getWebsites()
      .then((res) => {
        // const result = res.reduce((acc, website) => ({
        //   hash: {...acc.hash, [website._id]: website}
        // }), { hash: {} })
        return {
          hash: res,
        }
      })
      .catch(() => toastr.error(intl.get('initialLoads.getWebsites.error')))
  },
]

export const picklist = [
  'settingsService',
  'picklistService',
  'storyService',
  'toastr',
  '$location',
  (settingsService, picklistService, storyService, toastr, $location) => {
    return settingsService
      .fetchPicklists()
      .then(async (res) => {
        if ($location.path().includes('/edit/')) {
          // When loading a story we may need to add its content_code to the corresponding picklist
          const story = await storyService.getStory()
          const storyPaywallStatus = _.get(
            story,
            'content_restrictions.content_code'
          )
          const currentPaywallStatusPicklistItem = _.get(
            res.data.picklists,
            'content_restrictions.picklist',
            []
          ).find((picklistItem) => picklistItem.id === storyPaywallStatus)

          if (storyPaywallStatus && !currentPaywallStatusPicklistItem) {
            if (
              !res.data.picklists.content_restrictions ||
              !res.data.picklists.content_restrictions.picklist
            ) {
              _.set(res.data.picklists, 'content_restrictions.picklist', [])
            }
            res.data.picklists.content_restrictions.picklist.push({
              active: true,
              default: false,
              id: storyPaywallStatus,
              text: storyPaywallStatus,
            })
          }
        }
        if (res.data) {
          picklistService.set(res.data)
          return res.data
        }
        return {}
      })
      .catch((e) => {
        return handlePicklistsError(e, toastr)
      })
  },
]

const fetchSettings = (force) => (settingsService, toastr, $rootScope) =>
  settingsService
    .fetchSettings(force)
    .then((res) => {
      // Setting global settings
      $rootScope.globalSettings = res.data
      // TODO: I don't know why we are doing global settings this way
      $rootScope.companySettings = res.data.v2
      return res.data
    })
    .catch((e) => {
      console.warn('Not able to retrieve settings.', e)
      toastr.error(intl.get('initialLoads.settings.error'))
      return {}
    })

export const settings = [
  'settingsService',
  'toastr',
  '$rootScope',
  fetchSettings(false),
]

export const reloadSettings = [
  'settingsService',
  'toastr',
  '$rootScope',
  fetchSettings(true),
]

export const distributorList = [
  'toastr',
  '$rootScope',
  (toastr, $rootScope) => {
    return getDistributors()
      .then(({ rows }) => {
        $rootScope.distributorList = rows
      })
      .catch(() => {
        // Suppress the warning message for now
        // toastr.warning(intl.get('initialLoads.distributorList.error'))
      })
  },
]

export const tagsSettings = [
  'settingsService',
  'tagsService',
  'toastr',
  (settingsService, tagsService, toastr) => {
    return settingsService
      .fetchTagsSettings()
      .then((res) => {
        tagsService.setTagsSettings(res)
        return res
      })
      .catch((e) => {
        console.warn('Not able to retrieve settings.', e)
        toastr.error(intl.get('initialLoads.settings.error'))
        return {}
      })
  },
]

export const lockState = [
  'padlockService',
  '$route',
  '$ngRedux',
  'featureFlagService',
  'toastr',
  async function (
    padlockService,
    $route,
    $ngRedux,
    featureFlagService,
    toastr
  ) {
    const storyId = $route.current.params.id
    const state = $ngRedux.getState()
    const currentLock = getLockDetails(state)

    const notifyReturnLockDetails = (lockDetails) => {
      $ngRedux.dispatch(lockDetailsAction(lockDetails))
      return {
        lockDetails,
      }
    }

    if (!storyId) {
      // no id is a new story
      return notifyReturnLockDetails({
        isMyLock: true,
      })
    }

    // If we have an id, it is an existing story
    if (!storyLockedAccessor(state)) {
      return notifyReturnLockDetails(currentLock)
    }

    const featureFlag = await featureFlagService.instance()
    if (featureFlag.isEnabled('draftApiStateful')) {
      try {
        const {
          isMyLock,
          readonly,
          lockedBy,
          lockedByDisplayName = intl.get('padlock.knockToast.unknownUser'),
        } = await getDocumentLockDetails(storyId)

        if (isMyLock) {
          setLastActiveTime(storyId)
          return notifyReturnLockDetails({
            isMyLock,
            readonly,
            lockedBy,
          })
        } else if (!lockedBy) {
          const createLockResponse = await acquireDocumentLock(storyId)
          setIsValidTab(storyId)
          return notifyReturnLockDetails(createLockResponse)
        } else {
          setIsValidTab(storyId)
          return notifyReturnLockDetails({
            isMyLock,
            readonly,
            lockedBy,
            lockedByDisplayName,
          })
        }
      } catch (error) {
        setIsValidTab(storyId)
        toastr.error(intl.get('initialLoads.lockStoryError'))
        return notifyReturnLockDetails({
          isMyLock: false,
          readonly: true,
          lockedBy: undefined,
        })
      }
    } else {
      try {
        const lockDetails = await padlockService.obtainLock(storyId)
        return notifyReturnLockDetails(lockDetails)
      } catch (lockDetails) {
        return notifyReturnLockDetails(lockDetails)
      }
    }
  },
]

/**
 * Redirects to the new editor if the feature flag is ON
 * for the organization and the user has not forced V1 in the URL
 *
 * COMP-7529
 */
function handleV2Routing(loadDetails, isForcedV1) {
  if (isForcedV1) {
    return
  }

  const { templateId, storyId } = loadDetails
  window.localStorage.removeItem(NEW_EDITOR_EXP)
  if (storyId) {
    window.location.href = EDIT_STORY_EDITOR_UI_ROUTE(loadDetails.storyId)
  } else if (templateId && templateId !== 'default') {
    window.location.href = NEW_STORY_WITH_TEMPLATE_EDITOR_UI_ROUTE(
      loadDetails.templateId
    )
  } else {
    window.location.href = NEW_STORY_EDITOR_UI_ROUTE()
  }
}

export const story = [
  '$rootScope',
  'storyService',
  '$route',
  'toastr',
  'featureFlagService',
  'evidentlyService',
  'settingsService',
  '$routeParams',
  async function (
    $rootScope,
    storyService,
    $route,
    toastr,
    featureFlagService,
    evidentlyService,
    settingsService
  ) {
    const flags = await evidentlyService.instance()
    const loadDetails = {
      storyId: $route.current.params.id,
      branchId: $route.current.params.branch,
      revisionId: $route.current.params.revision,
      templateId: $route.current.params.template,
      isTemplate: false,
    }

    const hasV1Dropped = flags['ui-v1-dropoff']

    const url = new URL(window.location.href)
    const params = url.searchParams
    const isLocalDev = document.location.hostname.indexOf('localhost') >= 0
    const isUserDev =
      window.localStorage.getItem('arc.email')?.split('@')[1] === 'washpost.com'
    const isForcedV1 =
      params.get('allowAngular') === 'true' && (isUserDev || isLocalDev)

    /**
     * COMP-7748: https://arcpublishing.atlassian.net/browse/COMP-7748
     *
     * This will validate if the legacyEnabled is false and the user has not forced V1
     */
    const openBeta = $rootScope.globalSettings?.openBeta || {}
    let shouldRedirectToV2 = false

    if (!openBeta.legacyEnabled && openBeta.enabled) {
      shouldRedirectToV2 = true
    }

    if (hasV1Dropped || shouldRedirectToV2) {
      window.localStorage.setItem(NEW_EDITOR_EXP, 'true')
      handleV2Routing(loadDetails, isForcedV1)
    }

    let v2Enabled = false
    const userEnabledNewEditor = window.localStorage.getItem(NEW_EDITOR_EXP)
    if (!$rootScope.globalSettings) {
      // sometimes settings haven't been loaded yet
      await settingsService.fetchSettings(false)
    }
    if ($rootScope.globalSettings?.openBeta?.enabled) {
      if (userEnabledNewEditor === undefined || userEnabledNewEditor === null) {
        v2Enabled = $rootScope.globalSettings?.openBeta?.default ?? false
      } else {
        v2Enabled = userEnabledNewEditor === 'true'
      }
    }

    if (v2Enabled && !isForcedV1) {
      if (loadDetails.storyId) {
        window.location.href = EDIT_STORY_EDITOR_UI_ROUTE(loadDetails.storyId)
      } else if (
        loadDetails.templateId &&
        loadDetails.templateId !== 'default'
      ) {
        window.location.href = NEW_STORY_WITH_TEMPLATE_EDITOR_UI_ROUTE(
          loadDetails.templateId
        )
      } else {
        window.location.href = NEW_STORY_EDITOR_UI_ROUTE()
      }
    }

    const loadingToast = toastr.info(
      intl.get('initialLoads.story.loading'),
      null,
      {
        timeOut: 20000,
      }
    )
    return storyService.initialLoad(loadDetails).then(async (story) => {
      if (
        story.additional_properties &&
        story.additional_properties._NOT_DENORMALIZED
      ) {
        delete story.additional_properties._NOT_DENORMALIZED
      } else {
        toastr.clear(loadingToast)
        let storyLoadedMsg = intl.get('initialLoads.story.loaded')

        if (window.localStorage.showTakeoverSuccess === 'true') {
          window.localStorage.removeItem('showTakeoverSuccess')
          storyLoadedMsg += intl.get('initialLoads.story.takeover')
        }

        toastr.success(storyLoadedMsg)

        if (localStorage.getItem('circulationStoryCreateError')) {
          if (
            JSON.parse(localStorage.getItem('circulationStoryCreateError'))
              .storyId === story._id
          ) {
            toastr.error(intl.get('initialLoads.story.circulations.error'))
            localStorage.removeItem('circulationStoryCreateError')
          }
        }

        if (localStorage.getItem('circulationStoryCloneError')) {
          if (
            JSON.parse(localStorage.getItem('circulationStoryCloneError'))
              .storyId === story._id
          ) {
            toastr.error(intl.get('documentActionsToolbar.clone.error'))
            localStorage.removeItem('circulationStoryCloneError')
          }
        }
      }
      // Title for story page
      if (!story._id)
        document.title = `${intl.get(
          'browserTitle.story.newStory'
        )} - Composer | ${intl.get('browserTitle.story.main')}`
      else if (!story.headlines.basic)
        document.title = `Composer | ${intl.get('browserTitle.story.main')}`
      else
        document.title = `${story.headlines.basic} - Composer | ${intl.get(
          'browserTitle.story.main'
        )}`

      $rootScope.story = story

      const result = {
        story,
      }

      const featureFlag = await featureFlagService.instance()
      if (featureFlag.isEnabled('draftApiStateful')) {
        await storyService.getVariants($route.current.params.id)
      }

      return result
    })
  },
]

// TODO: Mike : replace storyService with storyTemplateService
// For now we are doing templates this way so we don't explode the story component
export const template = [
  '$rootScope',
  'storyService',
  '$route',
  'toastr',
  'featureFlagService',
  function ($rootScope, storyService, $route, toastr) {
    // Default template id if there isn't one
    // let templateId = $route.current.params.template || 'Default'
    const loadDetails = {
      storyId: '',
      revisionId: '',
      templateId: $route.current.params.template,
      isTemplate: true,
    }
    // dont need to inflate, that happens in story service
    return storyService.initialLoad(loadDetails).then(async (story) => {
      toastr.clear()
      $rootScope.story = story

      const result = {
        story,
      }

      return result
    })
  },
]

export const websked = [
  '$rootScope',
  'webskedService',
  'storyService',
  'pharosService',
  'settingsService',
  function (
    root,
    webskedService,
    storyService,
    pharosService,
    { fetchCompanySettings }
  ) {
    return fetchCompanySettings('org').then((res) => {
      if (!res) {
        return {}
      }
      // don't load websked for template page
      if (
        res.data.disableWebskedIntegration ||
        _.get(storyService, 'loadDetails.isTemplate', false)
      ) {
        return {}
      } else {
        return webskedService.getWebskedStatus().then(
          (res) => {
            return {
              buttons: res.data,
              enum: res.data.reduce((acc, o) => {
                return {
                  ...acc,
                  [o.id]: o.name,
                }
              }, {}),
            }
          },
          (err) => {
            console.log(err)

            return {
              webskedDown: true,
            }
          }
        )
      }
    })
  },
]
